import { CheckIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';

import { comparableFeatures } from '../../utils/pricing/tiers';
import type {
  ComparableFeature,
  FeatureDescription,
  FeatureParameters,
  PriceVariables,
  Tier,
} from '../../utils/pricing/types';

interface PricingFeaturesProps {
  tier: Tier;
  variables: PriceVariables;
  noMargin?: boolean;
}

export function PricingFeatures(props: PricingFeaturesProps) {
  const { tier, variables, noMargin = false } = props;

  return (
    <ul
      role="list"
      className={clsx(
        tier.featured ? 'text-neutral-300' : 'text-neutral-600',
        'space-y-3 text-sm/6',
        !noMargin && 'mt-8 xl:mt-10',
      )}
    >
      {comparableFeatures.map((feature) => (
        <ComparableFeatures key={feature} {...props} feature={feature} />
      ))}

      {tier.additionalFeatures.map((additionalFeature) => {
        const feature = getFeature(additionalFeature, variables);

        if (feature === null) {
          return null;
        }

        return (
          <AdditionalFeatures
            key={feature.value}
            feature={feature}
            tier={tier}
          />
        );
      })}
    </ul>
  );
}

interface ComprabaleFeatureProps
  extends Omit<PricingFeaturesProps, 'noMargin'> {
  feature: ComparableFeature;
}

function ComparableFeatures(props: ComprabaleFeatureProps) {
  const { tier, feature, variables } = props;
  const value = tier.comparisonFeatures[feature];
  const feat = getFeature(value, variables);

  if (!feat) {
    return null;
  }

  return <AdditionalFeatures feature={feat} tier={tier} />;
}

interface AdditionalFeaturesProps extends Pick<PricingFeaturesProps, 'tier'> {
  feature: FeatureParameters;
}

function AdditionalFeatures(props: AdditionalFeaturesProps) {
  const { feature, tier } = props;
  const className = clsx(
    tier.featured ? 'text-white' : 'text-primary-600',
    'h-5 w-5',
  );
  const icon =
    feature.icon === 'double-check' ? (
      <DoubleCheckIcon aria-hidden="true" className={className} />
    ) : (
      <CheckIcon className={className} />
    );
  return (
    <li className="flex gap-x-3">
      <div>{icon}</div>
      <div
        className={clsx(
          feature.emphasis ? 'font-semibold' : '',
          'truncate flex-1',
        )}
      >
        {feature.value}
      </div>
    </li>
  );
}

function DoubleCheckIcon(props: { className?: string }) {
  const { className } = props;
  return (
    <div className="relative">
      <CheckIcon
        aria-hidden="true"
        className={clsx('h-6 w-5 flex-none', className)}
      />
      <CheckIcon
        aria-hidden="true"
        className={clsx(
          'h-6 w-5 scale-[80%] origin-top-left flex-none absolute top-0 translate-x-[1px] -translate-y-[2px] left-0 text-primary-600',
          className,
        )}
      />
    </div>
  );
}

function getFeature(
  feature: FeatureDescription,
  variables: PriceVariables,
): FeatureParameters | null {
  if (typeof feature === 'string') {
    return {
      value: feature,
    };
  } else if (typeof feature === 'function') {
    const computedFeature = feature(variables);
    if (typeof computedFeature === 'string') {
      return getFeature(computedFeature, variables);
    } else {
      return computedFeature;
    }
  } else {
    return feature;
  }
}
